import React from "react"

import { MapPin, Navigation } from "lucide-react"
import { Marker } from "react-map-gl/mapbox"

import { MapCard, RouteIdentity } from "@/components/collections/monitoring/MapOverlayCard"
import { formatDuration, getDisplayRouteDelayMinutes } from "@/lib/routeDelayDisplay"
import { cn } from "@/lib/utils"
import { Route } from "@/types/collections"

/** In the order the map legend lists them. */
export const DRIVER_STATUS_COLOURS = [
  { key: "on_time", label: "On Time", colour: "#22C55E" },
  { key: "delayed", label: "Delayed", colour: "#EF4444" },
  { key: "ahead", label: "Ahead", colour: "#4ADE80" },
  { key: "completed", label: "Completed", colour: "#6B7280" },
  { key: "not_started", label: "Not Started", colour: "#3B82F6" },
] as const

const colourOf = (key: (typeof DRIVER_STATUS_COLOURS)[number]["key"]) =>
  DRIVER_STATUS_COLOURS.find((s) => s.key === key)!.colour

const getMarkerColor = (status: string, delayMinutes: number) => {
  if (status === "completed") return colourOf("completed")
  if (status === "not_started") return colourOf("not_started")
  if (status === "delayed" || delayMinutes >= 5) return colourOf("delayed")
  if (status === "ahead" || delayMinutes <= -5) return colourOf("ahead")
  return colourOf("on_time")
}

interface DriverMarkerProps {
  route: Route
  lat: number
  lng: number
  isSelected: boolean
  isHovered: boolean
  onHoverChange: (route: Route | null) => void
  onSelect: (route: Route) => void
}

const DriverMarker: React.FC<DriverMarkerProps> = ({
  route,
  lat,
  lng,
  isSelected,
  isHovered,
  onHoverChange,
  onSelect,
}) => (
  <Marker
    longitude={lng}
    latitude={lat}
    anchor="center"
    style={{ zIndex: isHovered ? 1000 : 50 }}
    onClick={(e) => {
      e.originalEvent.stopPropagation()
      onSelect(route)
    }}
  >
    <div
      className={cn(
        "group relative cursor-pointer transition-transform",
        (isSelected || isHovered) && "scale-110"
      )}
      onMouseEnter={() => onHoverChange(route)}
      onMouseLeave={() => onHoverChange(null)}
    >
      <div className="pointer-events-none absolute bottom-full left-1/2 z-[70] mb-2 hidden -translate-x-1/2 group-hover:block">
        <MapCard>
          <div className="p-2.5">
            <RouteIdentity code={route.code} driverName={route.driverName} />
          </div>
          <div className="flex items-center gap-3 border-t border-palette-jungleGreen100 px-2.5 py-1.5 text-[10px] text-palette-jungleGreen500">
            <span className="flex items-center gap-1">
              <MapPin size={10} className="flex-shrink-0" />
              {route.completedStops}/{route.totalStops} stops
            </span>
            {route.vehicleReg && (
              <span className="ml-auto truncate font-medium text-palette-jungleGreen400">
                {route.vehicleReg}
              </span>
            )}
          </div>
          {getDisplayRouteDelayMinutes(route) !== 0 && (
            <div
              className={cn(
                "border-t border-palette-jungleGreen100 px-2.5 py-1.5 text-[11px] font-medium",
                getDisplayRouteDelayMinutes(route) >= 5 ? "text-red-600" : "text-green-600"
              )}
            >
              {getDisplayRouteDelayMinutes(route) > 0
                ? `${formatDuration(getDisplayRouteDelayMinutes(route))} late`
                : `${formatDuration(Math.abs(getDisplayRouteDelayMinutes(route)))} ahead`}
            </div>
          )}
          <div className="bg-palette-jungleGreen000 px-2.5 py-1.5 text-[10px] text-palette-jungleGreen400">
            Click to select route
          </div>
        </MapCard>
      </div>

      {(getDisplayRouteDelayMinutes(route) >= 5 || getDisplayRouteDelayMinutes(route) <= -5) && (
        <div
          className={cn(
            "absolute -top-5 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-bold text-white shadow-md",
            getDisplayRouteDelayMinutes(route) >= 5 ? "bg-red-500" : "bg-green-400"
          )}
        >
          {getDisplayRouteDelayMinutes(route) > 0
            ? `+${getDisplayRouteDelayMinutes(route)}`
            : getDisplayRouteDelayMinutes(route)}
          m
        </div>
      )}

      {getDisplayRouteDelayMinutes(route) >= 5 && route.status !== "completed" && (
        <div className="absolute -right-0.5 -top-0.5 z-10 h-2.5 w-2.5 rounded-full border-2 border-white bg-red-500 shadow-md" />
      )}

      {isSelected && (
        <div className="absolute inset-0 -m-0.5 h-8 w-8 animate-ping rounded-full bg-amber-500/40" />
      )}

      <div
        className={cn(
          "flex h-7 w-7 items-center justify-center rounded-full border-2 border-white shadow-lg",
          isSelected && "ring-2 ring-amber-400 ring-offset-1"
        )}
        style={{
          backgroundColor: getMarkerColor(route.status, getDisplayRouteDelayMinutes(route)),
        }}
      >
        <Navigation size={14} className="text-white" />
      </div>
    </div>
  </Marker>
)

export default DriverMarker
